import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Mountain, Map, Layers, Waves, Cloud, TreePine, Globe2, BookOpen } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const iconMap = { Mountain, Map, Layers, Waves, Cloud, TreePine, Globe2, BookOpen };

const defaultImages = [
  `${API}/api/files/dersimizcografiya/uploads/47ef7813-7734-4f04-bad2-41faed93bdbd.jpg`,
  `${API}/api/files/dersimizcografiya/uploads/8323a863-2eaa-456c-8e55-ee3c07bfd141.jpg`,
  `${API}/api/files/dersimizcografiya/uploads/f959a2d9-7cfe-459c-b0bf-42d8f29bae91.jpg`,
  `${API}/api/files/dersimizcografiya/uploads/6c1e8512-f1b6-40d6-8b84-3c9e14379c1a.jpg`,
  `${API}/api/files/dersimizcografiya/uploads/7b1de8e6-cf40-4f99-be57-48941bc4ef9a.jpg`,
  `${API}/api/files/dersimizcografiya/uploads/ef2756c6-5b2c-4b74-a57a-2a8e9f5b7a0c.jpg`,
];

const defaultCategories = [
  { id: 0, title: 'Fiziki Coğrafiya', icon: 'Mountain', desc: 'Relyef, iqlim, su ehtiyatları, təbii proseslər',
    fullDesc: 'Fiziki coğrafiya Yer kürəsinin təbii komponentlərini — relyefi, iqlimi, su ehtiyatlarını, torpağı və bitki örtüyünü öyrənir. Bu sahə dağların yaranması, çayların formalaşması, iqlim qurşaqlarının xüsusiyyətləri və təbii fəlakətlərin səbəblərini araşdırır.',
    topics: ['Litosfer və relyef formaları', 'Atmosfer və iqlim qurşaqları', 'Hidrosfer — çaylar, göllər, okeanlar', 'Torpaq örtüyü və eroziya', 'Təbii fəlakətlər və onların səbəbləri'] },
  { id: 1, title: 'İqtisadi Coğrafiya', icon: 'Map', desc: 'Əhali, təsərrüfat, dünya ölkələri',
    fullDesc: 'İqtisadi coğrafiya dünya ölkələrinin iqtisadi inkişafını, əhali məskunlaşmasını, təbii ehtiyatların istifadəsini və beynəlxalq ticarət əlaqələrini öyrənir.',
    topics: ['Əhali coğrafiyası və demoqrafiya', 'Dünya ölkələrinin iqtisadiyyatı', 'Təbii ehtiyatlar və istifadəsi', 'Sənaye və kənd təsərrüfatı', 'Beynəlxalq iqtisadi əlaqələr'] },
  { id: 2, title: 'Xəritəşünaslıq', icon: 'Layers', desc: 'Xəritə oxuma, koordinat sistemi, miqyas',
    fullDesc: 'Xəritəşünaslıq xəritələrin yaradılması, oxunması və istifadəsi haqqında elmdir. Bu sahə koordinat sistemi, proyeksiyalar, miqyas və müasir GIS texnologiyalarını əhatə edir.',
    topics: ['Coğrafi koordinat sistemi', 'Xəritə proyeksiyaları', 'Miqyas və məsafə hesablama', 'Topoqrafik xəritələr', 'GIS və müasir xəritəçəkmə'] },
  { id: 3, title: 'Okeanologiya', icon: 'Waves', desc: 'Okean və dənizlər, su axınları, gel-git',
    fullDesc: 'Okeanologiya dünya okeanlarını, dənizləri, onların fiziki, kimyəvi və bioloji xüsusiyyətlərini öyrənir. Su axınları, gel-git hadisələri və dəniz altı relyefi bu sahənin əsas mövzularıdır.',
    topics: ['Dünya okeanlarının xüsusiyyətləri', 'Dəniz axınları və su dövranı', 'Gel-git hadisələri', 'Dəniz altı relyefi', 'Okeanların ekoloji problemləri'] },
  { id: 4, title: 'Meteorologiya', icon: 'Cloud', desc: 'Atmosfer hadisələri, hava proqnozu',
    fullDesc: 'Meteorologiya atmosferdəki fiziki prosesləri, hava hadisələrini və iqlim dəyişikliklərini öyrənir. Hava proqnozu, atmosfer təzyiqi, küləklər və yağıntılar bu sahənin əsas mövzularıdır.',
    topics: ['Atmosferin quruluşu və tərkibi', 'Hava kütlələri və frontlar', 'Siklon və antisiklonlar', 'Hava proqnozu metodları', 'İqlim dəyişiklikləri'] },
  { id: 5, title: 'Biocoğrafiya', icon: 'TreePine', desc: 'Təbii zonalar, flora və fauna',
    fullDesc: 'Biocoğrafiya canlı orqanizmlərin Yer kürəsində yayılma qanunauyğunluqlarını öyrənir. Təbii zonalar, meşələr, çöllər, tundra və digər biomlar bu sahənin əsas tədqiqat obyektləridir.',
    topics: ['Təbii zonalar və biomlar', 'Meşə ekosistemlər', 'Səhra və yarımsəhra zonaları', 'Qütb və subarktik zonalar', 'Biomüxtəlifliyin qorunması'] },
];

export default function CategoryPage() {
  const { id } = useParams();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const catIndex = parseInt(id) || 0;

  useEffect(() => {
    axios.get(`${API}/api/site-content`).then(r => setContent(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  const cat = content.categories_section || {};
  const items = cat.items || [];
  const item = items[catIndex] || {};
  const def = defaultCategories[catIndex] || defaultCategories[0];

  const title = item.title || def.title;
  const desc = item.desc || def.desc;
  const fullDesc = item.full_desc || def.fullDesc;
  const topics = item.topics || def.topics;
  const image = item.image || defaultImages[catIndex] || '';
  const gallery = item.gallery || [];
  const Icon = iconMap[item.icon || def.icon] || Globe2;

  // Get other categories for "Digər Kateqoriyalar"
  const others = defaultCategories.filter(c => c.id !== catIndex).slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          {image ? <img src={image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#00B4D8]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/60 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Əsas Səhifəyə qayıt
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-[#00B4D8]/30 text-[#48CAE4] text-xs font-medium mb-1">Kateqoriya</span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">{title}</h1>
              </div>
            </div>
            <p className="text-lg text-white/80 max-w-2xl">{desc}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <p className="text-lg text-[#5C6B7A] leading-relaxed mb-10">{fullDesc}</p>

            {/* Topics */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1A2530] mb-6">Əsas Mövzular</h2>
              <div className="space-y-3">
                {(Array.isArray(topics) ? topics : []).map((topic, i) => (
                  <motion.div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[#F4F7F6] border border-[#E2E8F0]"
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{i + 1}</span>
                    </div>
                    <span className="text-[#1A2530] font-medium">{topic}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Gallery - 3 images from admin */}
            {gallery.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#1A2530] mb-6">Vizual Materiallar</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {gallery.map((img, i) => (
                    <a key={i} href={img} target="_blank" rel="noopener noreferrer" className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow group">
                      <img src={img} alt={`${title} - ${i+1}`} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Other Categories */}
      <section className="py-16 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-bold text-[#1A2530] mb-8">Digər Kateqoriyalar</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map(other => {
              const OIcon = iconMap[other.icon] || Globe2;
              const otherItem = (items[other.id]) || {};
              const otherImg = otherItem.image || defaultImages[other.id] || '';
              return (
                <Link key={other.id} to={`/category/${other.id}`} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
                  <div className="relative h-40 overflow-hidden">
                    {otherImg ? <img src={otherImg} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full bg-gradient-to-br from-[#0077B6] to-[#00B4D8]" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0077B6]/60 to-transparent" />
                    <div className="absolute top-3 right-3 p-2 rounded-xl bg-white/20 backdrop-blur-sm"><OIcon className="w-5 h-5 text-white" /></div>
                    <div className="absolute bottom-3 left-4"><span className="text-white font-bold">{otherItem.title || other.title}</span></div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-[#5C6B7A]">{otherItem.desc || other.desc}</p>
                    <span className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-[#0077B6]">Ətraflı <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
